import { useState, useEffect } from "react";
import { getMeta, META_KEYS } from "../../data/local/db";
import type { LocalStaff } from "../../data/local/db";
import { cachedAuthorizers, refreshAuthorizers } from "../../data/local/staff-cache";
import { verifyPin } from "../../platform/auth/pin";
import { PinPad } from "./PinPad";

/**
 * Manager authorization for a restricted action (T126, FR-056).
 *
 * THIS MUST WORK WITH NO NETWORK. A void during an outage is exactly when a
 * manager's approval matters most, and a prompt that needs the server would
 * mean the one control that protects the till stops working at the one moment
 * it is needed. So verification happens against the PIN hash replicated into
 * IndexedDB, using the same PBKDF2 module the Worker uses — one implementation,
 * so an offline approval and an online one cannot disagree.
 *
 * WHAT THIS RECORDS AND WHY. The result names BOTH people: the staff member
 * performing the action and the manager who approved it. A journal entry
 * showing only "voided by the manager" would hide who actually did it, and one
 * showing only the cashier would hide that anyone approved. Both, always.
 *
 * The server re-verifies when the envelope syncs (`POST /api/auth/authorize`
 * covers the online path). Offline approval is therefore provisional in exactly
 * the way an offline sale is: recorded now, reconciled later, never blocked.
 */

export interface Authorization {
  managerId: string;
  managerName: string;
  authorizedAt: string;
}

export interface ManagerAuthProps {
  /** Shown to the manager so they know what they are approving. */
  action: string;
  /** Extra context, e.g. "Void sale T01-000123 · ₱450.00". */
  detail?: string;
  onAuthorized: (authorization: Authorization) => void;
  onCancel: () => void;
}

/**
 * Local lockout after repeated failures.
 *
 * Deliberately modest and in-memory: a four-digit PIN has about 13 bits of
 * entropy, so throttling is the real defence, but an offline terminal cannot
 * enforce a durable lockout without handing anyone with the device a way to
 * lock out every manager. The durable counter lives server-side (FR-053); this
 * is the part that has to work with no network.
 */
const MAX_ATTEMPTS = 5;

/** Why the terminal has nobody to offer — each cause needs a different remedy. */
type EmptyReason =
  /** The session was refused, so credentials could not be fetched. */
  | "unauthenticated"
  /** Never replicated and no line to fetch them now. */
  | "unreachable"
  /** Replication worked and genuinely nobody can authorize. */
  | "none";

export function ManagerAuth({ action, detail, onAuthorized, onCancel }: ManagerAuthProps) {
  /** `null` while still looking — an empty list is a conclusion, not a start. */
  const [managers, setManagers] = useState<LocalStaff[] | null>(null);
  const [emptyReason, setEmptyReason] = useState<EmptyReason>("none");
  const [selected, setSelected] = useState<LocalStaff | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    void (async () => {
      // `canAuthorize` is a property of the ROLE, replicated onto the staff
      // record so this list can be built with no network.
      let all = await cachedAuthorizers();

      /**
       * Nothing replicated yet — the case that strands a correction. Try once,
       * here, with whatever session this terminal holds.
       *
       * Replication normally happens at sign-in, and asking again on every
       * approval would be wasteful. But "normally" is doing a lot of work in
       * that sentence: a terminal signed in before the feature shipped, one
       * whose sign-in fetch failed, and one restored from a cache all reach
       * this dialog with an empty list. Retrying at the moment of need costs
       * one request and turns a dead end into a working approval.
       */
      if (all.length === 0) {
        const token = await getMeta<string>(META_KEYS.sessionToken);
        const outcome = token
          ? await refreshAuthorizers(token)
          : ({ ok: false, reason: "unauthenticated" } as const);

        all = await cachedAuthorizers();
        if (all.length === 0) setEmptyReason(outcome.ok ? "none" : outcome.reason);
      }

      setManagers(all);
      if (all.length === 1) setSelected(all[0] ?? null);
    })();
  }, []);

  const lockedOut = attempts >= MAX_ATTEMPTS;

  async function submit(pin: string) {
    if (!selected || lockedOut) return;

    setChecking(true);
    setError(null);

    try {
      const ok = await verifyPin(pin, {
        hash: selected.pinHash,
        salt: selected.pinSalt,
        iterations: selected.pinIterations,
      });

      if (!ok) {
        const next = attempts + 1;
        setAttempts(next);
        setError(
          next >= MAX_ATTEMPTS
            ? "Too many incorrect attempts. Ask a manager to sign in on the terminal."
            : "Incorrect PIN.",
        );
        return;
      }

      onAuthorized({
        managerId: selected.id,
        managerName: selected.name,
        authorizedAt: new Date().toISOString(),
      });
    } finally {
      setChecking(false);
    }
  }

  if (managers === null) {
    return (
      <div className="managerauth" role="dialog" aria-label="Manager approval needed">
        <h2 className="managerauth__title">Manager approval needed</h2>
        <p className="managerauth__detail">Checking who can approve this…</p>
      </div>
    );
  }

  if (managers.length === 0) {
    /**
     * Say which of the three it is.
     *
     * "No manager is set up" was true of all three and useful for none of
     * them: an expired session, a terminal that has never been online since
     * sign-in, and a deployment with no authorizing role all read the same,
     * and only the last one is actually about setup. The person reading this
     * has a queue waiting and needs to know whether to sign in again, find a
     * connection, or stop trying.
     */
    const remedy =
      emptyReason === "unauthenticated"
        ? "This terminal's session has expired, so it could not fetch who may approve. Sign out and sign in again — approvals work offline from then on."
        : emptyReason === "unreachable"
          ? "Manager credentials have not reached this terminal yet, and there is no connection to fetch them now. Connect once and they are stored for offline use."
          : "Nobody on this deployment has a role that can authorize. Grant one in the back office.";

    return (
      <div className="managerauth" role="dialog" aria-label="Manager approval needed">
        <h2 className="managerauth__title">Manager approval needed</h2>
        <p className="managerauth__error">{remedy}</p>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="managerauth" role="dialog" aria-label="Manager approval needed">
      <h2 className="managerauth__title">Manager approval needed</h2>
      <p className="managerauth__action">{action}</p>
      {detail && <p className="managerauth__detail">{detail}</p>}

      {!selected ? (
        <ul className="managerauth__people">
          {managers.map((manager) => (
            <li key={manager.id}>
              <button type="button" onClick={() => setSelected(manager)}>
                {manager.name}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <>
          <p className="managerauth__who">{selected.name}</p>
          <PinPad
            label={`${selected.name}'s PIN`}
            onSubmit={submit}
            busy={checking || lockedOut}
            error={error}
          />
          {managers.length > 1 && (
            <button
              type="button"
              className="managerauth__switch"
              onClick={() => {
                setSelected(null);
                setError(null);
              }}
            >
              Someone else
            </button>
          )}
        </>
      )}

      {/* The PIN pad shows its own error once a manager is chosen; this covers
          the selection step, where there is no pad yet. */}
      {error && !selected && (
        <p className="managerauth__error" role="alert">
          {error}
        </p>
      )}

      <button type="button" className="managerauth__cancel" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

export default ManagerAuth;
